'use client';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class QueryCache {
  private cache = new Map<string, CacheEntry<any>>();
  // Increased from 30s → 5 minutes for stable reference data (crew, licenses, routes)
  private readonly DEFAULT_TTL = 300_000;
  // In-flight request deduplication: key → Promise
  private inflight = new Map<string, Promise<any>>();

  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    this.cache.set(key, { data, timestamp: Date.now(), ttl });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    return entry.data as T;
  }

  /**
   * Stale-while-revalidate: return cached data immediately (even if stale)
   * while triggering a background refresh. Eliminates loading spinners on
   * subsequent visits to the same screen.
   */
  getStale<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    return entry.data as T;
  }

  isStale(key: string, ttl?: number): boolean {
    const entry = this.cache.get(key);
    if (!entry) return true;
    const effectiveTtl = ttl ?? entry.ttl;
    return Date.now() - entry.timestamp > effectiveTtl;
  }

  /**
   * Deduplicates concurrent fetches for the same key.
   * If a fetch for `key` is already in-flight, returns the same Promise
   * instead of firing a second network request.
   */
  async dedupe<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
    // Return cached if fresh
    const cached = this.get<T>(key);
    if (cached !== null) return cached;

    // Return in-flight promise if already fetching
    const existing = this.inflight.get(key);
    if (existing) return existing as Promise<T>;

    // Start new fetch and register it
    const promise = fetchFn().finally(() => {
      this.inflight.delete(key);
    });
    this.inflight.set(key, promise);
    return promise;
  }

  invalidate(key: string): void {
    this.cache.delete(key);
    this.inflight.delete(key);
  }

  invalidatePrefix(prefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
    for (const key of this.inflight.keys()) {
      if (key.startsWith(prefix)) {
        this.inflight.delete(key);
      }
    }
  }

  clear(): void {
    this.cache.clear();
    this.inflight.clear();
  }
}

export const queryCache = new QueryCache();

// ─── Search memoization ───────────────────────────────────────────────────────

/**
 * Builds a stable cache key from a base name and a params object.
 * Sorts keys so {status:'Active',depot:'Main'} === {depot:'Main',status:'Active'}.
 */
export function buildSearchKey(base: string, params: Record<string, unknown>): string {
  const sorted = Object.keys(params)
    .sort()
    .reduce<Record<string, unknown>>((acc, k) => {
      if (params[k] !== undefined && params[k] !== null && params[k] !== '') {
        acc[k] = params[k];
      }
      return acc;
    }, {});
  return `${base}:${JSON.stringify(sorted)}`;
}

/**
 * Client-side memoized filter over an already-loaded array.
 * Returns a stable reference when inputs haven't changed.
 */
const memoStore = new Map<string, { inputRef: unknown; result: unknown }>();

export function memoFilter<T>(
  key: string,
  items: T[],
  filterFn: (items: T[]) => T[]
): T[] {
  const existing = memoStore.get(key);
  if (existing && existing.inputRef === items) {
    return existing.result as T[];
  }
  const result = filterFn(items);
  memoStore.set(key, { inputRef: items, result });
  return result;
}

/** Clears a memoFilter entry when the underlying data changes. */
export function clearMemo(key: string): void {
  memoStore.delete(key);
}

/** Clears all memoFilter entries whose key starts with prefix. */
export function clearMemoPrefix(prefix: string): void {
  for (const key of memoStore.keys()) {
    if (key.startsWith(prefix)) memoStore.delete(key);
  }
}
